/**
 * UNALLOCATED SALES (SHORT SIDE) — left column.
 *
 * Mirror Access form `frmMatchingPSMatch_Stock_Sales` (cột trái L=204/2856).
 * Order theo Top position trong Access:
 *   Section header → Contract Sales combo → mini-header "Unmatched Open Sales"
 *   → Quantity KG → Quality → Client → Delivery To
 *   → Instructed - Allocated (s_total_matched + s_matched cùng row) → Unallocated.
 */
import { Autocomplete, Stack, TextField, Typography } from '@mui/material'

import type { StockSalesContractOption } from '@/store/api/matchingPsApi'

import { formatStandard } from './stockSalesUtils'

interface Props {
  contractOptions: StockSalesContractOption[]
  contractValue: StockSalesContractOption | null
  onContractChange: (opt: StockSalesContractOption | null) => void
  sClient: string
  sQuality: string
  sQuantity: number | null
  sDeliveryDate: string
  sTotalMatched: number | null
  sMatched: number | null
  sUnmatched: number | null
}

const labelSx = { width: 130, flexShrink: 0, fontSize: '0.78rem' }
const compactInputSx = { '& .MuiInputBase-input': { py: 0.5 } }
const highlightInputSx = {
  '& .MuiInputBase-input': {
    py: 0.5,
    color: '#1F467D',
    fontWeight: 700,
  },
  '& .MuiOutlinedInput-root': { bgcolor: '#E9ECED' },
}
const compactAutocompleteSx = {
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { py: 0, pl: '6px' },
  '& .MuiAutocomplete-input': { py: '3px !important', fontSize: '0.8rem' },
}
const readonlyTextField = {
  size: 'small' as const,
  InputProps: { readOnly: true, sx: { fontSize: '0.8rem' } },
  fullWidth: true,
  sx: compactInputSx,
}
const highlightReadonlyTextField = {
  size: 'small' as const,
  InputProps: { readOnly: true, sx: { fontSize: '0.8rem' } },
  fullWidth: true,
  sx: highlightInputSx,
}

export default function SalesSidePanel({
  contractOptions,
  contractValue,
  onContractChange,
  sClient,
  sQuality,
  sQuantity,
  sDeliveryDate,
  sTotalMatched,
  sMatched,
  sUnmatched,
}: Props) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
          lineHeight: 1.3,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}
      >
        Unallocated Sales (Short Side)
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Contract Sales
        </Typography>
        <Autocomplete
          options={contractOptions}
          value={contractValue}
          onChange={(_, v) => onContractChange(v)}
          getOptionLabel={(o) => o.merge_value}
          isOptionEqualToValue={(a, b) => a.s_side === b.s_side && a.stock_side === b.stock_side}
          renderOption={(props, option) => (
            <li {...props} key={`${option.s_side}__${option.stock_side}`}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ width: '100%', fontSize: '0.8rem' }}
              >
                <span style={{ flex: '0 0 220px' }}>{option.merge_value}</span>
                <span style={{ flex: '0 0 80px', color: '#666' }}>{option.quality ?? ''}</span>
                <span style={{ flex: 1, color: '#666' }}>{option.client_short ?? ''}</span>
              </Stack>
            </li>
          )}
          size="small"
          fullWidth
          sx={compactAutocompleteSx}
          renderInput={(params) => <TextField {...params} />}
        />
      </Stack>

      {/* Floating mini-header trên row Quantity KG (Access T=1140) */}
      <Typography
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          fontSize: '0.7rem',
          mt: 0,
        }}
      >
        Unmatched Open Sales
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Quantity KG
        </Typography>
        <TextField
          {...readonlyTextField}
          value={sQuantity == null ? '' : formatStandard(sQuantity)}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Quality
        </Typography>
        <TextField {...readonlyTextField} value={sQuality} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Client
        </Typography>
        <TextField {...readonlyTextField} value={sClient} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Delivery To
        </Typography>
        <TextField {...readonlyTextField} value={sDeliveryDate} />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Instructed - Allocated
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            {...highlightReadonlyTextField}
            value={sTotalMatched == null ? '' : formatStandard(sTotalMatched)}
          />
          <TextField
            {...highlightReadonlyTextField}
            value={sMatched == null ? '' : formatStandard(sMatched)}
          />
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={labelSx}>
          Unallocated
        </Typography>
        <TextField
          {...highlightReadonlyTextField}
          value={sUnmatched == null ? '' : formatStandard(sUnmatched)}
        />
      </Stack>
    </Stack>
  )
}
